import { showNotification } from "@mantine/notifications"
import { useMutation, useQuery } from "@tanstack/react-query"
import { formatQuery, RuleGroupType } from "react-querybuilder"
import { useNavigate } from "react-router-dom"

import { BillyRequests, Period } from "@costory/types/endpoints/billy"
import {
  DigestResponses,
  DigestType,
  OutputDigestType,
  Position,
} from "@costory/types/endpoints/digest"
import { GraphResponses } from "@costory/types/endpoints/graph"
import { Filters } from "@costory/types/filters"
import {
  BillyContributionAnalysisCategory,
  DatePresetDigest,
} from "@costory/types/prisma-client"

import { apiClient } from "@costory/front/lib/apiClient"
import { queryClient } from "@costory/front/lib/queryClient"
import { getQueryFormatted } from "@costory/front/pages/DigestV2/helper"

export const useDigestListQuery = () => {
  return useQuery({
    queryKey: ["digests"],
    queryFn: async () => {
      const response =
        await apiClient.get<DigestResponses.ListDigests[]>("/digest/list")
      return response.data
    },
  })
}

export const useAvailableDataDigestQuery = () => {
  return useQuery({
    queryKey: ["digests", "data"],
    queryFn: async () => {
      const response = await apiClient.get<DigestResponses.AvailableDigests>(
        "/digest/available-data",
      )
      return response.data
    },
  })
}

// New query with refresh interval for checking digest availability
export const useAvailableDataDigestQueryWithRefresh = (
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["digests", "data", "refresh"],
    queryFn: async () => {
      const response = await apiClient.get<DigestResponses.AvailableDigests>(
        "/digest/available-data",
      )
      return response.data
    },
    enabled,
    refetchInterval: enabled ? 5000 : false, // Refresh every 5 seconds when enabled
    refetchIntervalInBackground: false,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
  })
}

export const useDeleteDigestMutation = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<DigestResponses.Digest>(
        `/digest/edit/${id}`,
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["digests"] })
      showNotification({
        position: "top-right",
        color: "green",
        title: "Digest Archived!",
        message: "Digest Archived successfully",
      })
    },
    onError: (error) => {
      showNotification({
        color: "red",
        position: "top-right",
        title: "Error archiving digest",
        message: error.message,
      })
    },
  })

export const useQueryDigestData = (
  preset: DatePresetDigest,
  period: string,
  digestId?: string,
) => {
  return useQuery({
    queryKey: ["digest", period, preset, digestId],
    queryFn: async () => {
      // Pass digestId as a path parameter if present
      const url = digestId
        ? `digest/data/${period}/${preset}/${digestId}`
        : `digest/data/${period}/${preset}`
      const response = await apiClient.get<DigestResponses.Investigation>(url)
      return response.data
    },
  })
}

export const useNewDigestMutation = () => {
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async (data: DigestType) => {
      const response = await apiClient.post<DigestResponses.Digest>(
        "/digest/new",
        data,
      )
      return response.data
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["digests"] })
      // Update onboarding immediately when a digest is created
      await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] })
      navigate(`/digests/edit/${data.id}`)
      showNotification({
        color: "green",
        position: "top-right",
        title: "Digest Created!",
        message: "Digest Created",
      })
    },
    onError: (error) => {
      showNotification({
        color: "red",
        position: "top-right",
        title: "Error creating digest",
        message: error.message,
      })
    },
  })
}

export const useSummaryDigestQuery = (id: string, limit?: number) => {
  return useQuery({
    queryKey: ["digest", "summary", id, limit],
    queryFn: async () => {
      const response = await apiClient.get<DigestResponses.DigestExport>(
        `/digest/summary/${id}/${limit}`,
      )
      return response.data
    },
  })
}

export const useQueryOpenDigest = (id: string) => {
  return useQuery({
    queryKey: ["digest", id],
    queryFn: async () => {
      const response = await apiClient.get<DigestResponses.Digest>(
        `/digest/edit/${id}`,
      )

      return response.data
    },
  })
}

export const useEditDigestMutation = (id: string) =>
  useMutation({
    mutationFn: async (data: DigestType) => {
      const response = await apiClient.post<DigestResponses.Digest>(
        `/digest/edit/${id}`,
        data,
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["digests"] })
      await queryClient.invalidateQueries({
        queryKey: ["digest", id],
      })

      showNotification({
        color: "green",
        title: "Digest Saved!",
        position: "top-right",
        message: "Digest Saved",
      })
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error saving digest",
        position: "top-right",
        message: error.message,
      })
    },
  })

export const useGetDigestConfigQuery = (digestId?: string | undefined) => {
  return useQuery({
    queryKey: ["digest", "config", digestId],
    queryFn: async () => {
      const url = digestId ? `/digest/config/${digestId}` : "/digest/config"
      const response = await apiClient.get<DigestResponses.DigestConfig>(url)
      return response.data
    },
  })
}

export const useEditDigestConfigMutation = ({
  closeModal,
}: {
  closeModal: () => void
}) => {
  return useMutation({
    mutationFn: async (data: DigestResponses.DigestConfig) => {
      const response = await apiClient.post<DigestResponses.DigestConfig>(
        "/digest/config/edit",
        {
          ...data,
          whereClause: data?.whereClause
            ? JSON.parse(formatQuery(data.whereClause, "json_without_ids"))
            : undefined,
        },
      )
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["digest", "config"] })
      await queryClient.invalidateQueries({ queryKey: ["digest"] })
      await queryClient.invalidateQueries({ queryKey: ["digests", "data"] })
      showNotification({
        color: "green",
        title: "Digest Config Saved!",
        position: "top-right",
        message: "Updated digest will be available in a few minutes",
      })
      closeModal()
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error editing digest config",
        position: "top-right",
        message: error.message,
      })
    },
  })
}

export const useEmailDigestReportMutation = () => {
  return useMutation({
    mutationFn: async (config: OutputDigestType) => {
      const response = await apiClient.post("/digest/email", config)
      return response.data
    },
    onSuccess: async () => {
      showNotification({
        color: "green",
        title: "Digest Report Sent!",
        position: "top-right",
        message: "Digest report sent successfully",
      })
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error sending digest report",
        position: "top-right",
        message: error.message,
      })
    },
  })
}

export const useContributionAnalysisMutation = ({
  whereClause,
  currentPeriod,
  previousPeriod,
  exclusionWhereClause,
  costColumn,
  category = BillyContributionAnalysisCategory.CONTRIBUTION_ANALYSIS,
}: {
  whereClause: Position
  currentPeriod: Period
  previousPeriod: Period
  exclusionWhereClause: RuleGroupType
  costColumn: string
  category?: BillyContributionAnalysisCategory
}) => {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<BillyRequests.ContributionAnalysis>(
        "/billy/contribution-factors-analysis",
        {
          whereClause,
          currentPeriod,
          previousPeriod,
          exclusionWhereClause,
          costColumn,
          category,
        },
      )
      return response.data
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["contribution-analysis-available"],
      })
    },
  })
}

export function useAddExplorerImageToNote() {
  return useMutation({
    mutationFn: async ({
      filters,
      setEditContent,
      showComparisonChart,
    }: {
      filters: Filters
      setEditContent: React.Dispatch<React.SetStateAction<string>>
      showComparisonChart?: boolean
    }) => {
      const response = await apiClient.post<GraphResponses.Url>("/graph/url", {
        filters,
        showComparisonChart,
      })
      return { response, filters, setEditContent }
    },
    onSuccess: ({
      response,
      filters,
      setEditContent,
    }: {
      response: { data: GraphResponses.Url }
      filters: Filters
      setEditContent: React.Dispatch<React.SetStateAction<string>>
    }) => {
      const url = `${window.location.origin}/explorer?${getQueryFormatted(filters)}`
      const content = `  \n[![img](${response.data.url})](${url})    \n`
      setEditContent((prev) => prev + content)
    },
    onError: () =>
      showNotification({
        color: "red",
        title: "Error",
        message: "Failed to generate chart. Please try again.",
      }),
  })
}

export const useGetTreeQuery = (
  whereClause: RuleGroupType,
  columns: string[],
) => {
  return useQuery({
    queryKey: ["tree", whereClause, columns],
    queryFn: async () => {
      const response = await apiClient.post<DigestResponses.GetTree>(
        "/digest/tree",
        {
          whereClause,
          columns,
        },
      )
      return response.data
    },
    enabled: columns.length > 0,
  })
}
